import type {
  Pet,
  PetEvolutionStage,
  PetSpecies,
  PetAttribute,
  PetEvolution,
} from '.';
import { setAttribute } from './stats/petStats.modifiers';
import { getAttributeValue } from './pet.getters';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import PetNames from 'src/replicant/airtable/PetNames';

export const petModifiers = (
  Pet: Pet,
  { History, Encyclopedia, Fate, Level }: typeof Pet.$DEPENDENCIES,
) => (state: Pet.MutableState) => ({
  addEvolution: (
    species: PetSpecies,
    stage: PetEvolutionStage,
    timestamp: number,
  ) => {
    state.pet.evolutions.push({ species, stage, timestamp });
  },

  /**
   * Clear evolution path after reborn
   */
  resetEvolution: () => {
    state.pet.evolutions = [];
  },

  /**
   * Save current attributes values to state
   * Used before adding new evolution to avoid attribute interpolation issues related to different species decline speed
   */
  syncAttributes: (state: Pet.MutableState, now: number) => {
    const attributes = state.pet.attributes;
    const attributeIDs = Object.keys(attributes) as PetAttribute[];

    attributeIDs.forEach((attribute) => {
      // Get current value interpolation
      const value = getAttributeValue(state, attribute, now);

      // Save value and update updateAt value
      setAttribute(state, attribute, value, now);
    });
  },

  /**
   * Spawn new pet
   */
  spawnPet: <T extends 'debut' | 'death'>(
    reason: T,
    newPet: Extract<PetEvolution, { stage: 'baby' }>,
    now: number,
    random: () => number,
    ...[friendID]: T extends 'debut' ? [string] : []
  ) => {
    const { getPetAge, getCurrentEvolution, getPetName } = Pet.getters(state);
    const { addEvolution, resetEvolution } = Pet.modifiers(state);
    const { resolveStatusEffect } = Fate.api;

    // Collect history data
    const previousPetAge = getPetAge(now);
    const previousEvolution = getCurrentEvolution();
    const previousSpecies = previousEvolution?.species;
    const previousStage = previousEvolution?.stage;
    const previousPetName = getPetName();
    const previousPetEvolutions = [...state.pet.evolutions];

    // Assign pet name
    const { pet, settings } = state;
    const localeSuffix = settings.locale === 'ja' ? 'jp' : 'en';
    pet.name = getRandomItemFromArray(PetNames, random)[`name_${localeSuffix}`];

    // Drop previous evolutions
    resetEvolution();

    // Add new evolutions
    addEvolution(newPet.id, newPet.stage, now);

    // Set starting attributes
    setAttribute(state, 'love', newPet.love_ini, now);
    setAttribute(state, 'dirtiness', newPet.dirtiness_ini, now);
    setAttribute(state, 'stomach', newPet.stomach_ini, now);
    setAttribute(state, 'toothAcidity', newPet.tooth_acidity_ini, now);
    setAttribute(state, 'urine', newPet.urine_ini, now);

    // Clear all status effects
    resolveStatusEffect(state, 'asleep', false, now);
    resolveStatusEffect(state, 'evolving', false, now);
    resolveStatusEffect(state, 'scared', false, now);
    resolveStatusEffect(state, 'sick', false, now);
    resolveStatusEffect(state, 'toothDecay', false, now);
    resolveStatusEffect(state, 'dying', false, now);
    resolveStatusEffect(state, 'dead', false, now);
    resolveStatusEffect(state, 'hadVisitor', false, now);
    resolveStatusEffect(state, 'wantToGoOut', false, now);

    // Determine fate
    Fate.api.update(state, now);

    // Update encyclopedia
    Encyclopedia.modifiers(state).setSpeciesState(newPet.id, 'unlocked');

    // Increase pets raised counter
    state.petRaised++;

    // Highest pet age
    if (state.highestPetAge < previousPetAge) {
      state.highestPetAge = previousPetAge;
    }

    if (reason === 'debut') {
      // Save history record about pet duo
      History.api.addPlayerDuoRecord(state, {
        friendID,
        species: previousSpecies,
        name: previousPetName,
        age: previousPetAge,
        evolutions: previousPetEvolutions,
        timestamp: now,
      });
    } else if (reason === 'death') {
      // Save history record about pet death
      History.api.addDeathRecord(state, {
        species: previousSpecies,
        name: previousPetName,
        age: previousPetAge,
        evolutions: previousPetEvolutions,
        timestamp: now,
      });
    }

    // Add XP reward
    Level.modifiers(state).incrementXp(reason, previousStage);
  },
});
